import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Mail, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

type UnsubscribeStatus = "loading" | "valid" | "invalid" | "success" | "error";

interface EmailPreferences {
  email_pref_digest: boolean;
  email_pref_likes: boolean;
  email_pref_matches: boolean;
  email_pref_messages: boolean;
  email_pref_marketing: boolean;
  email_pref_subscription: boolean;
}

const PREFERENCE_LABELS: Record<keyof EmailPreferences, { label: string; description: string }> = {
  email_pref_digest: { 
    label: "Resumo diário", 
    description: "Receba um resumo das atividades no seu perfil" 
  },
  email_pref_likes: { 
    label: "Novos likes", 
    description: "Quando alguém curtir seu perfil" 
  },
  email_pref_matches: { 
    label: "Novos matches", 
    description: "Quando acontecer uma conexão mútua" 
  },
  email_pref_messages: { 
    label: "Novas mensagens", 
    description: "Quando receber uma mensagem no chat" 
  },
  email_pref_marketing: { 
    label: "Novidades e promoções", 
    description: "Ofertas especiais e atualizações da plataforma" 
  },
  email_pref_subscription: { 
    label: "Alertas de assinatura", 
    description: "Lembretes sobre sua assinatura" 
  },
};

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");
  
  const [status, setStatus] = useState<UnsubscribeStatus>("loading");
  const [username, setUsername] = useState<string>("");
  const [preferences, setPreferences] = useState<EmailPreferences>({
    email_pref_digest: true,
    email_pref_likes: true,
    email_pref_matches: true,
    email_pref_messages: true,
    email_pref_marketing: true,
    email_pref_subscription: true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [unsubscribeAll, setUnsubscribeAll] = useState(false);

  useEffect(() => {
    async function validateUser() {
      if (!uid) {
        setStatus("invalid");
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("username, email_pref_digest, email_pref_likes, email_pref_matches, email_pref_messages, email_pref_marketing, email_pref_subscription, unsubscribed_from_emails")
          .eq("id", uid)
          .single();

        if (error || !profile) {
          setStatus("invalid");
          return;
        }

        setUsername(profile.username || "Usuário");
        setUnsubscribeAll(profile.unsubscribed_from_emails || false);
        setPreferences({
          email_pref_digest: profile.email_pref_digest ?? true,
          email_pref_likes: profile.email_pref_likes ?? true,
          email_pref_matches: profile.email_pref_matches ?? true,
          email_pref_messages: profile.email_pref_messages ?? true,
          email_pref_marketing: profile.email_pref_marketing ?? true,
          email_pref_subscription: profile.email_pref_subscription ?? true,
        });
        setStatus("valid");
      } catch {
        setStatus("invalid");
      }
    }

    validateUser();
  }, [uid]);

  const handlePreferenceChange = (key: keyof EmailPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleUnsubscribeAll = () => {
    const newValue = !unsubscribeAll;
    setUnsubscribeAll(newValue);
    if (newValue) {
      // If unsubscribing from all, disable all preferences
      setPreferences({
        email_pref_digest: false,
        email_pref_likes: false,
        email_pref_matches: false,
        email_pref_messages: false,
        email_pref_marketing: false,
        email_pref_subscription: false,
      });
    }
  };

  const handleSave = async () => {
    if (!uid) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          ...preferences,
          unsubscribed_from_emails: unsubscribeAll,
        })
        .eq("id", uid);

      if (error) throw error;

      setStatus("success");
      toast.success("Preferências salvas com sucesso!");
    } catch (error) {
      setStatus("error");
      toast.error("Erro ao salvar preferências");
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (status === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Validando...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid UID
  if (status === "invalid") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Link Inválido</CardTitle>
            <CardDescription>
              Este link de cancelamento de inscrição não é válido ou expirou.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/">
              <Button variant="outline">Voltar para a página inicial</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
            <CardTitle>Preferências Atualizadas</CardTitle>
            <CardDescription>
              Suas preferências de email foram salvas com sucesso.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              {unsubscribeAll 
                ? "Você não receberá mais emails da Interlaced."
                : "Você continuará recebendo apenas os emails selecionados."}
            </p>
            <Link to="/auth">
              <Button className="bg-gradient-to-r from-[#C4406B] to-[#9F3764] text-white">
                Fazer login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main preferences form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Preferências de Email</CardTitle>
          <CardDescription>
            Olá, {username}! Escolha quais emails deseja receber.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Unsubscribe all toggle */}
          <div className="flex items-center space-x-3 p-4 rounded-lg bg-destructive/5 border border-destructive/20">
            <Checkbox
              id="unsubscribe-all"
              checked={unsubscribeAll}
              onCheckedChange={handleUnsubscribeAll}
            />
            <div className="flex-1">
              <label 
                htmlFor="unsubscribe-all" 
                className="text-sm font-medium cursor-pointer text-destructive"
              >
                Cancelar todos os emails
              </label>
              <p className="text-xs text-muted-foreground">
                Você não receberá mais nenhum email da Interlaced
              </p>
            </div>
          </div>

          {/* Individual preferences */}
          <div className={`space-y-4 ${unsubscribeAll ? 'opacity-50 pointer-events-none' : ''}`}>
            <p className="text-sm font-medium text-foreground">Ou escolha individualmente:</p>
            
            {(Object.entries(PREFERENCE_LABELS) as [keyof EmailPreferences, typeof PREFERENCE_LABELS[keyof EmailPreferences]][]).map(([key, { label, description }]) => (
              <div key={key} className="flex items-start space-x-3">
                <Checkbox
                  id={key}
                  checked={preferences[key]}
                  onCheckedChange={() => handlePreferenceChange(key)}
                  disabled={unsubscribeAll}
                />
                <div className="flex-1">
                  <label 
                    htmlFor={key} 
                    className="text-sm font-medium cursor-pointer text-foreground"
                  >
                    {label}
                  </label>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
              </div>
            ))}
          </div>

          <Button 
            onClick={handleSave} 
            disabled={isSaving}
            className="w-full bg-gradient-to-r from-[#C4406B] to-[#9F3764] text-white"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              "Salvar preferências"
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Você pode alterar suas preferências a qualquer momento nas configurações da sua conta.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
